<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="$t('setting.shift.header')" :subTitle="$t('setting.shift.description')" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButtonBack class="me-2" href="/setting" />
          <McButton
            v-if="
              getAccessRight() &&
              hasAccessAll(['users:create', 'c17a-default-shift:create']) &&
              !noData
            "
            icon="icon-outline_plus"
            :text="$t('global.create')"
            @click="$router.push({ name: 'Setting Shift Create' })"
          />
        </div>
      </div>
    </div>
    <section class="mc-shift">
      <McShiftTimeLine :shifts="shifts" :show-preview="true" />
      <McGrapqlTable
        :apiClient="query"
        :fields="fields"
        :customHandlers="customHandlers"
        :hasFilter="false"
        :hasDelete="hasAccess('c17a-default-shift:delete') ? true : false"
        :isEmpty="noData"
        :busy="isLoading"
        :refreshData="!isLoading"
        trackBy="shiftId"
        :hasCheckbox="getAccessRight()"
        searchable
        withCard
        @available-change="handleAvailableChange"
      >
        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <McFilterSelect
            :defaultColumnOrder="initialFields"
            :currentColumnOrder="dataFields"
            @setColumn="handleSetDataFields"
          />
        </template>

        <template #cell(name)="{ item }">
          <a class="fw-bold text-primary td__noellipsis clickable-field" @click="toDetail(item)">
            <div class="d-flex align-items-center justify-content-between gap-2">
              {{ item.name }}
              <span class="mc-shift-color-bar" :style="{ backgroundColor: '#' + item.color }">
                <i :style="{ backgroundColor: '#' + item.color }"></i>
              </span>
            </div>
          </a>
        </template>

        <!-- Jam Mulai -->
        <template #cell(startTime)="{ item }"> {{ item.startTime }} WIB </template>

        <!-- Jam Berakhir -->
        <template #cell(endTime)="{ item }">
          {{ item.endTime }} WIB
          <span v-if="item.isOvernight" class="text-muted">(+1 hari)</span>
        </template>

        <!-- Pengulangan -->
        <template #cell(days)="data">
          {{ formatDays(data.value || data.item.days) }}
        </template>

        <template #emptyState>
          <McEmptyState
            class="pb-4"
            :title="$t('maintenance.nolist.noData')"
            :desc="$t('master.service.emptyState.desc')"
            :hasButton="getAccessRight() && hasAccess('c17a-default-shift:create')"
            @buttonPrimaryAction="customHandlers.handleAddEmpty"
          />
        </template>
      </McGrapqlTable>
    </section>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import McShiftTimeLine from './components/McShiftTimeLine.vue'
import McEmptyState from '@/components/McEmptyState'
import { DELETE_SHIFT, GET_SHIFTS } from '@/services/api/setting/shift/shift'

export default {
  page: {
    title: 'Master Shift',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McShiftTimeLine,
    McEmptyState,
  },
  mixins: [Helper],
  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          return this.onRowClicked(item)
        },

        handleAddEmpty: () => {
          return this.$router.push({ name: 'Setting Shift Create' })
        },
      },

      noData: false,
      isLoading: true,
      shifts: [],
    }
  },
  computed: {
    /**
     * Total no. of records
     */
    title() {
      return this.$t('master.shift')
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$t('setting.shift.shiftName'),
          sortable: true,
        },
        {
          key: 'startTime',
          label: this.$t('setting.shift.startTime'),
          sortable: true,
        },
        {
          key: 'endTime',
          label: this.$t('setting.shift.endTime'),
          sortable: true,
        },
        {
          key: 'days',
          label: this.$t('setting.shift.repetition'),
          sortable: false,
        },
      ]
    },
    query() {
      return {
        get: async (filter) => {
          const data = await this.getData(filter)
          return data
        },
        delete: async (id) => {
          const res = await this.$apollo.mutate({
            mutation: DELETE_SHIFT,
            variables: {
              input: { shiftId: id },
            },
          })

          const updatedShifts = this.$store.state.shift.shifts.filter(
            (shift) => shift.shiftId !== id
          )
          this.$store.dispatch('shift/setShifts', updatedShifts)

          return res.data.deleteShift
        },
      }
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
  },
  methods: {
    async getData(filter) {
      const res = await this.$apollo.query({
        query: GET_SHIFTS,
        fetchPolicy: 'no-cache',
        variables: {
          filter,
        },
      })

      const apiShifts = res.data?.shifts?.shifts || {}
      const rawData = apiShifts.data || []

      const rows = rawData.map((shift) => {
        if (!Array.isArray(shift.shiftTime) || shift.shiftTime.length === 0) {
          return []
        }

        // show only the latest time
        // others just for historical record and for handling delayed alert
        const shiftTime = shift.shiftTime[0]
        const startTime = shiftTime.startTime
        const endTime = shiftTime.endTime
        const days = shiftTime.days || []

        const [sh, sm] = (startTime || '00:00').split(':').map(Number)
        const [eh, em] = (endTime || '00:00').split(':').map(Number)
        const startMinutes = (sh || 0) * 60 + (sm || 0)
        const endMinutes = (eh || 0) * 60 + (em || 0)
        const isOvernight = endMinutes < startMinutes

        return {
          id: shiftTime.shiftTimeId,
          shiftId: shift.shiftId,
          name: shift.name,
          color: shift.color,
          days,
          startTime,
          endTime,
          isOvernight,
        }
      })

      this.shifts = rows
      this.noData = rows.length === 0

      this.$store.dispatch('shift/setShifts', this.shifts)

      return {
        ...apiShifts,
        data: rows,
      }
    },

    onRowClicked(item) {
      if (!this.hasAccessAll(['users:update', 'd5a-default-mdkaryawan:read'])) return

      this.$router.push({
        name: 'Master Employee Detail',
        params: { id: item.userId },
      })
    },
    handleAvailableChange(val) {
      this.noData = !val
      this.isLoading = false
    },
    formatDays(days) {
      if (!days || !Array.isArray(days) || days.length === 0) {
        return '-'
      }

      if (days.length === 7) {
        return this.$t('global.days.everyday')
      }

      const dayLabels = {
        1: this.$t('global.days.monday').slice(0, 3),
        2: this.$t('global.days.tuesday').slice(0, 3),
        3: this.$t('global.days.wednesday').slice(0, 3),
        4: this.$t('global.days.thursday').slice(0, 3),
        5: this.$t('global.days.friday').slice(0, 3),
        6: this.$t('global.days.saturday').slice(0, 3),
        7: this.$t('global.days.sunday').slice(0, 3),
      }

      return [...days]
        .sort((a, b) => a - b)
        .map((d) => dayLabels[d] || d)
        .join(', ')
    },

    formatTime(time, isOvernight = false) {
      return isOvernight ? `${time} WIB (+1 hari)` : `${time} WIB`
    },

    toDetail(item) {
      this.$router.push({ name: 'Setting Shift Detail', params: { id: item.shiftId } })
    },
  },
  mounted() {
    this.isLoading = false
  },
}
</script>
<style lang="scss" scoped>
.mc-shift-color-bar {
  width: 6px;
  height: 32px;
  border-radius: 3px 0 0 3px;
  display: inline-block;
  flex-shrink: 0;
  position: relative;
  margin-right: 25px;

  i {
    content: '';
    position: absolute;
    top: 0;
    left: 6px;
    width: 20px;
    height: 100%;
    border-radius: 0 3px 3px 0;
    opacity: 0.12;
  }
}
</style>
